import React, { useState } from 'react';
import { canBuyCard } from '../../helper/GameBoardHelper';
import { getCardById, getNobleById } from '../../data/cardData';
import { MiniCard, CardTooltip, ReservedCard } from '../CardSection/Card';
import './PlayerPanel.css';

const GEM_COLORS = ['WHITE', 'BLUE', 'GREEN', 'RED', 'BLACK'];
const GEM_ALL = [...GEM_COLORS, 'GOLD'];
const COLOR_LABEL = { WHITE: '白', BLUE: '蓝', GREEN: '绿', RED: '红', BLACK: '黑', GOLD: '金' };
const TOKEN_IMG = {
  WHITE: '/cards/tokens/white.png', BLUE: '/cards/tokens/blue.png',
  GREEN: '/cards/tokens/green.png', RED: '/cards/tokens/red.png',
  BLACK: '/cards/tokens/black.png', GOLD: '/cards/tokens/gold.png',
};
const tokenStyle = (color) => ({ backgroundImage: `url(${process.env.PUBLIC_URL}${TOKEN_IMG[color]})`, backgroundSize: 'cover', backgroundPosition: 'center' });

/* ============ Noble Chip with hover tooltip ============ */
function NobleChip({ noble }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="pp-noble"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="pp-noble-crown">👑</span>
      <span className="pp-noble-pts">{noble.points}</span>
      {hovered && (
        <div className="pp-noble-tooltip">
          <div className="pp-noble-tooltip__title">👑 贵族 +{noble.points}</div>
          <div className="pp-noble-tooltip__req">
            {Object.entries(noble.requirement).map(([color, count]) => (
              <span key={color} className={`gem-pip ${color.toLowerCase()}`}>{count}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ Single Card Stack (one color) ============ */
function CardStack({ color, cards }) {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div className="pp-card-stack" title={`${COLOR_LABEL[color]} ×${cards.length}`}>
      <div className="pp-stack-cards" style={{ height: `${46 + Math.max(0, cards.length - 1) * 9}px` }}>
        {cards.map((card, i) => (
          <div
            key={card.id}
            className="pp-stack-item"
            style={{ top: `${i * 9}px`, zIndex: i }}
            onMouseEnter={() => setHoveredCard(card)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <MiniCard card={card} />
            {hoveredCard?.id === card.id && (
              <CardTooltip card={card} />
            )}
          </div>
        ))}
      </div>
      <span className="pp-stack-count">{cards.length}</span>
    </div>
  );
}

function EmptySlot() {
  return <span className="player-card__empty">—</span>;
}

function ReservedBack({ count }) {
  return (
    <div className="player-card__reserved-back" title={`预定卡 ×${count}`}>
      <span>×{count}</span>
    </div>
  );
}

/* ============ PlayerPanel ============ */
export default function PlayerPanel({
  gameState,
  currentUserId,
  userMap,
  interactable,
  onBuyCard,
}) {
  return (
    <div className="players-panel">
      <div className="players-panel__title">玩家</div>
      {gameState.turn_order.map((pid, idx) => {
        const ps = gameState.players[pid];
        if (!ps) return null;
        const isCurrent = idx === gameState.current_player_index;
        const isMe = Number(pid) === currentUserId;
        const tokenTotal = Object.values(ps.tokens).reduce((s, v) => s + v, 0);
        const playerName = userMap[pid] || `Player ${pid}`;

        // Group purchased cards by color
        const purchasedByColor = {};
        ps.purchased_cards.forEach((cardId) => {
          const card = getCardById(cardId);
          if (!card) return;
          const c = card.color;
          if (!purchasedByColor[c]) purchasedByColor[c] = [];
          purchasedByColor[c].push(card);
        });

        // Resolve acquired nobles
        const acquiredNobles = ps.acquired_nobles.map((nid) => getNobleById(nid)).filter(Boolean);

        return (
          <div key={pid} data-player-id={pid} className={`player-card${isCurrent ? ' active-turn' : ''}${isMe ? ' is-me' : ''}`}>
            <div className="player-card__header">
              <div className="player-card__identity">
                <strong>{playerName}{isMe && ' (我)'}</strong>
                <span>{isCurrent ? '当前回合' : '等待'} · token {tokenTotal}/10</span>
              </div>
              <div className="player-card__score" aria-label="prestige points">{ps.prestige_points}</div>
            </div>

            <div className="player-card__tokens">
              {GEM_ALL.map((color) => {
                const count = ps.tokens[color] || 0;
                return (
                  <span key={color} className={`player-token-slot ${count === 0 ? 'is-empty' : ''}`} title={`${COLOR_LABEL[color]}: ${count}`}>
                    <span className="player-token-count">{count}</span>
                    <span className={`player-token-icon ${color.toLowerCase()}`} style={tokenStyle(color)} />
                  </span>
                );
              })}
              <span className="player-token-total">Σ{tokenTotal}</span>
            </div>

            <div className="player-card__body">
              <div className="player-card__purchased">
                <div className="player-card__section-label">Purchased</div>
                <div className="player-card__stacks">
                  {ps.purchased_cards.length > 0 ? (
                    GEM_COLORS.map((color) => {
                      const stack = purchasedByColor[color];
                      if (!stack || stack.length === 0) return null;
                      return <CardStack key={color} color={color} cards={stack} />;
                    })
                  ) : (
                    <EmptySlot />
                  )}
                </div>
              </div>

              <div className="player-card__side">
                <div className="player-card__reserved">
                  <div className="player-card__section-label">Reserved</div>
                  <div className="player-card__reserved-row">
                    {ps.reserved_cards.length === 0 ? (
                      <EmptySlot />
                    ) : isMe ? (
                      ps.reserved_cards.map((card) => (
                        <ReservedCard
                          key={card.id}
                          card={card}
                          isMyTurn={interactable}
                          canBuy={!!ps && canBuyCard(card, ps)}
                          onBuy={onBuyCard}
                        />
                      ))
                    ) : (
                      <ReservedBack count={ps.reserved_cards.length} />
                    )}
                  </div>
                </div>

                <div className="player-card__nobles">
                  <div className="player-card__section-label">Nobles</div>
                  <div className="player-card__nobles-row">
                    {acquiredNobles.length > 0 ? (
                      acquiredNobles.map((noble) => (
                        <NobleChip key={noble.id} noble={noble} />
                      ))
                    ) : (
                      <EmptySlot />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="player-card__footer player-stats">
              <span>🃏 {ps.purchased_cards.length}</span>
              <span>📋 {ps.reserved_cards.length}</span>
              <span>👑 {acquiredNobles.length}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
